import urlcat from "urlcat";

import { ENABLE_NHS_TERMINOLOGY, ENABLE_TRUD } from "@/config/featureFlags";
import { MedicationFilters } from "@/interfaces/IServiceUser";
import { axiosInstance, parseResponse, TENANT_API_URL } from "@/services";

const MEDICATION_URL = `${TENANT_API_URL}/service-users/medication`;
const DMD_MEDICATION_URL = `${TENANT_API_URL}/service-users/dmd/autocomplete`;
const NHS_TERMINOLOGY_URL = `${TENANT_API_URL}/medication/autocomplete`;

// NHS England VMP medication autocomplete
export const autocompleteTrudMedication = async (query: string) => {
  if (!ENABLE_TRUD) return [];
  try {
    if (!query) return [];
    const response = await axiosInstance.get(DMD_MEDICATION_URL, {
      params: { q: query },
    });
    if (response.data?.success && Array.isArray(response.data.medications)) {
      return response.data.medications; // Return full object, including routes
    }
    return [];
  } catch (error: any) {
    return [];
  }
};

// NHS England VMP medication route lookup
export const lookupMedication = async (code: string) => {
  if (!ENABLE_NHS_TERMINOLOGY) return null;
  try {
    if (!code) return null;
    const response = await axiosInstance.get(
      `${TENANT_API_URL}/medication/lookup`,
      { params: { code } }
    );
    if (response.data?.medication) {
      return response.data;
    }
    return null;
  } catch (error: any) {
    return null;
  }
};

export const autocompleteNhsTerminologyMedication = async (query: string) => {
  if (!ENABLE_NHS_TERMINOLOGY) return [];
  try {
    if (!query) return [];
    const response = await axiosInstance.get(NHS_TERMINOLOGY_URL, {
      params: { q: query },
    });
    if (response.data?.success && Array.isArray(response.data.items)) {
      return response.data.items; // { code, display }
    }
    return [];
  } catch (error: any) {
    return [];
  }
};

export const getRosterMedications = async (data: Record<string, unknown>) => {
  try {
    const url = urlcat(`${MEDICATION_URL}/roster-visit-medications`, data);
    const response = await axiosInstance.get(url);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getTodayMedications = async () => {
  try {
    const response = await axiosInstance.get(`${MEDICATION_URL}/today`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getAllMedications = async () => {
  try {
    const response = await axiosInstance.get(`${MEDICATION_URL}`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getMedicationByUser = async (
  id: string,
  filters: MedicationFilters,
  signal?: AbortSignal
) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    // Generate the URL with query parameters from filters
    const url = urlcat(`${MEDICATION_URL}/${id}`, filters);
    const response = await axiosInstance.get(url, { signal });
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const createMedication = async (data: Record<string, unknown>) => {
  try {
    const response = await axiosInstance.post(`${MEDICATION_URL}`, data);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const updateMedication = async (
  id: string,
  data: Record<string, unknown>
) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.put(`${MEDICATION_URL}/${id}`, data);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const deleteMedication = async (id: string) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.delete(`${MEDICATION_URL}/${id}`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getMedicationRoutes = async () => {
  try {
    const response = await axiosInstance.get(`${MEDICATION_URL}/routes`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};
